/**
 * Base URL for API requests. Can be set using setBaseUrl function.
 * @example
 * setBaseUrl('http://api.example.com');
 * // All subsequent requests will be prefixed with this URL
 */
export let BASE_URL = "";
/**
 * Sets the base URL for all API requests.
 * @param url - The base URL to use for all API requests
 * @example
 * setBaseUrl('http://api.example.com');
 */
export function setBaseUrl(url) {
    BASE_URL = url;
}
/**
 * Provider for the active SaaS tenant id.
 *
 * The Surface webapp registers a provider that reads the `:tenantSlug` segment
 * from the current URL so every request automatically carries the
 * `saas-tenant-id` header that matches what the user is looking at, without
 * any per-call boilerplate.
 *
 * The api-client package itself does not assume the existence of a router or
 * any browser globals; it just calls the registered function and forwards the
 * value (if any) as a header.
 */
let activeTenantIdProvider = () => null;
/**
 * Registers a function that returns the current SaaS tenant id (or `null`).
 *
 * Surface UI calls this once at boot inside the router so that every API call
 * issued via `customFetch` gets a `saas-tenant-id` header derived from the URL.
 */
export function setActiveTenantIdProvider(provider) {
    activeTenantIdProvider = provider;
}
const isSameOriginApiUrl = (url) => {
    if (!url)
        return false;
    if (url.startsWith("/api/"))
        return true;
    if (url.startsWith("/"))
        return true; // relative -> same origin
    // For absolute URLs, only attach tenant headers to same-origin API paths.
    // This preserves security for notification payload URLs while still
    // preventing header leakage to third-party hosts.
    if (typeof window === "undefined")
        return false;
    try {
        const parsed = new URL(url);
        return (parsed.origin === window.location.origin &&
            parsed.pathname.startsWith("/api/"));
    }
    catch {
        return false;
    }
};
/** Normalizes query-string array params for the Python API. */
export const normalizeApiUrl = (url) => {
    if (!url.includes("?")) {
        return url;
    }
    const [baseUrl, queryString] = url.split("?");
    const searchParams = new URLSearchParams(queryString);
    const newParams = new URLSearchParams();
    for (const [key, value] of searchParams.entries()) {
        if (value.includes(",")) {
            // If we encounter commas, split them into multiple parameters (override default orval behavior of comma separated values for duplicate key values to match what the python API wants)
            const values = value.split(",");
            values.forEach((v) => {
                if (v)
                    newParams.append(key, v);
            });
        }
        else if (value && value !== "null" && value !== "undefined") {
            // Only append non-empty values (this is important, empty values will cause the API to return no results)
            newParams.append(key, value);
        }
    }
    const newQueryString = newParams.toString();
    return newQueryString ? `${baseUrl}?${newQueryString}` : baseUrl;
};
const resolveFinalUrl = (url) => {
    const normalizedUrl = normalizeApiUrl(url);
    return normalizedUrl.startsWith("http")
        ? normalizedUrl
        : BASE_URL + normalizedUrl;
};
const buildRequestHeaders = (url, options) => {
    const mergedHeaders = new Headers(options.headers ?? undefined);
    if (isSameOriginApiUrl(url) && !mergedHeaders.has("saas-tenant-id")) {
        const tenantId = activeTenantIdProvider();
        if (tenantId) {
            mergedHeaders.set("saas-tenant-id", tenantId);
        }
    }
    return mergedHeaders;
};
/**
 * Builds a fully-resolved URL and RequestInit with tenant headers and credentials.
 */
export const buildApiRequest = (url, options = {}) => {
    const normalizedUrl = normalizeApiUrl(url);
    const finalUrl = resolveFinalUrl(url);
    const mergedHeaders = buildRequestHeaders(normalizedUrl, options);
    return {
        finalUrl,
        requestInit: {
            credentials: "include",
            ...options,
            headers: mergedHeaders,
        },
    };
};
/**
 * Issues a fetch with shared URL normalization, tenant headers, and credentials.
 * Returns the raw Response (no body parsing).
 */
export const fetchResponse = async (url, options = {}) => {
    const { finalUrl, requestInit } = buildApiRequest(url, options);
    return fetch(finalUrl, requestInit);
};
/**
 * Known binary MIME types / prefixes. Mirrors Orval's binary content-type
 * whitelist so generated `Promise<Blob>` endpoints and runtime parsing agree.
 */
const BINARY_CONTENT_TYPE_MATCHERS = [
    "application/octet-stream",
    "application/pdf",
    "application/zip",
    "application/gzip",
    "application/x-gzip",
    "application/x-zip-compressed",
    "application/vnd.",
    "text/csv",
    "application/csv",
    "image/",
    "audio/",
    "video/",
];
/**
 * Returns true when `contentType` should be read as a Blob rather than text/JSON.
 */
export const isBinaryContentType = (contentType) => {
    if (!contentType) {
        return false;
    }
    const normalized = contentType.split(";")[0]?.trim().toLowerCase() ?? "";
    if (!normalized) {
        return false;
    }
    return BINARY_CONTENT_TYPE_MATCHERS.some((matcher) => normalized === matcher || normalized.startsWith(matcher));
};
const getRequestHeader = (headers, name) => {
    return new Headers(headers ?? undefined).get(name);
};
/**
 * True when the request Accept header explicitly asks for a binary payload.
 * Does not treat wildcard Accept values as binary — browsers send those by
 * default.
 */
const requestAcceptsBinary = (options) => {
    const accept = getRequestHeader(options.headers, "Accept");
    if (!accept) {
        return false;
    }
    return accept.split(",").some((part) => {
        const mediaType = part.split(";")[0]?.trim().toLowerCase() ?? "";
        return isBinaryContentType(mediaType);
    });
};
const parseJsonOrText = async (res) => {
    const text = await res.text();
    try {
        return text.length ? JSON.parse(text) : null;
    }
    catch {
        return text;
    }
};
/**
 * Parses a successful response body as Blob, JSON, or text based on
 * Content-Type (and, when Content-Type is missing, the request Accept header).
 */
const parseSuccessBody = async (res, options) => {
    const contentType = res.headers.get("Content-Type");
    if (isBinaryContentType(contentType) ||
        (!contentType && requestAcceptsBinary(options))) {
        return res.blob();
    }
    return parseJsonOrText(res);
};
const throwHttpError = async (res) => {
    // Error payloads are typically JSON/text even for binary download endpoints.
    const data = await parseJsonOrText(res);
    const error = new Error(`HTTP ${res.status}: ${res.statusText}`);
    error.status = res.status;
    error.data = data;
    throw error;
};
/**
 * Downloads binary content from an API URL (CSV, PDF, images).
 * Uses the same tenant header and credentials as `customFetch`.
 */
export const fetchBlob = async (url, options = {}) => {
    const res = await fetchResponse(url, options);
    if (!res.ok) {
        await throwHttpError(res);
    }
    return res.blob();
};
/**
 * Custom fetch function that properly handles array parameters in URLs and provides consistent response formatting.
 *
 * Features:
 * - Handles array parameters by splitting them into multiple query parameters
 * - Removes empty, null, and undefined parameters
 * - Automatically prepends BASE_URL to relative URLs
 * - Includes credentials by default
 * - Returns a `Blob` for binary Content-Types (or binary Accept when Content-Type is absent)
 * - Attempts to parse JSON responses, falls back to text
 * - Attaches the `saas-tenant-id` header on same-origin API calls when an
 *   active tenant id provider is registered (see {@link setActiveTenantIdProvider}).
 */
export const customFetch = async (url, options = {}) => {
    const res = await fetchResponse(url, options);
    if (!res.ok) {
        await throwHttpError(res);
    }
    return (await parseSuccessBody(res, options));
};
